import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Skeleton, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { RiQrCodeLine } from 'react-icons/ri';
import { debounce } from 'lodash';
import { fetchMyEvents } from '../../services/eventService';
import CustomPagination from '../../components/common/CustomPagination';
import EventQrModal from '../../components/events/EventQrModal';
import { BASE_URL } from '../../constants/Url';

// Date format helper: YYYY.MM.DD
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr).split('T')[0].replace(/-/g, '.');
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

export default function MyActivitiesPage({ isHide = false }) {
  const { t, i18n } = useTranslation();

  const [activeTab, setActiveTab] = useState('ongoing'); // 'ongoing' | 'expired'
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // QR Modal state
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const currentLang = i18n.language === 'en' ? 'en' : (i18n.language === 'zh-HK' ? 'zh-HK' : 'cn');

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchMyEvents({
        tab: activeTab,
        pageNo,
        pageSize,
      });

      if (res && (res.status === 200 || res.status === undefined) && Array.isArray(res.data)) {
        setEvents(res.data);
        setTotal(res.total !== undefined ? res.total : res.data.length);
      } else if (Array.isArray(res)) {
        setEvents(res);
        setTotal(res.length);
      } else {
        setEvents([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Failed to fetch events:', error);
      setEvents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(
    debounce(loadData, 200),
    [activeTab, pageNo, pageSize]
  );

  useEffect(() => {
    debouncedFetch();
    return debouncedFetch.cancel;
  }, [debouncedFetch]);

  // Reset page when tab changes
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      setActiveTab(newTab);
      setPageNo(1);
    }
  };

  // Open ticket QR modal
  const handleOpenQr = (e, item) => {
    e.stopPropagation();
    setSelectedEvent(item);
    setQrModalOpen(true);
  };

  // Render top-right corner status tag matching the exact UI design
  const renderStatusBadge = (item) => {
    if (item.isCheckedIn) {
      return (
        <div className="absolute top-0 right-0 px-3.5 py-1.5 rounded-bl-xl bg-[#F2F3F5] text-[#8F949C] text-xs font-normal tracking-wide select-none z-10">
          {t('text_status_checked_in')}
        </div>
      );
    }

    const status = item.approvalStatus;
    if (status === 'approved') {
      return (
        <div className="absolute top-0 right-0 px-3.5 py-1.5 rounded-bl-xl bg-[#EBF8F2] text-[#2AA566] text-xs font-normal tracking-wide select-none z-10">
          {t('text_status_approved')}
        </div>
      );
    }

    if (status === 'rejected') {
      return (
        <div className="absolute top-0 right-0 px-3.5 py-1.5 rounded-bl-xl bg-[#FDE8E8] text-[#E02424] text-xs font-normal tracking-wide select-none z-10">
          {t('text_status_rejected')}
        </div>
      );
    }

    // Default: pending / 待审批 (warm cream background, amber/brown text)
    return (
      <div className="absolute top-0 right-0 px-3.5 py-1.5 rounded-bl-xl bg-[#FFF8EC] text-[#D28228] text-xs font-normal tracking-wide select-none z-10">
        {t('text_status_pending')}
      </div>
    );
  };

  return (
    <div className={`w-full ${!isHide ? 'max-w-[1215px] mx-auto px-4 py-6 sm:py-8 min-h-[80vh]' : ''}`}>
      
      {/* ── Top Header when accessed as standalone page ── */}
      {!isHide && (
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 m-0">
            {t('text_my_activities')}
          </h1>
        </div>
      )}

      {/* ── Subtitle for embedded Desktop Profile View ── */}
      {isHide && (
        <div className="hidden sm:flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 m-0">
            {t('text_my_activities')}
          </h2>
        </div>
      )}

      {/* ── Tabs Segmented Bar ── */}
      <div className="flex items-center gap-2 mb-4 sm:mb-6">
        <button
          type="button"
          onClick={() => handleTabChange('ongoing')}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${
            activeTab === 'ongoing'
              ? 'bg-[#fdecf1] text-[#e50a47] shadow-sm'
              : 'bg-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          {t('text_event_ongoing')}
        </button>

        <button
          type="button"
          onClick={() => handleTabChange('expired')}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 ${
            activeTab === 'expired'
              ? 'bg-[#fdecf1] text-[#e50a47] shadow-sm'
              : 'bg-transparent text-gray-500 hover:text-gray-900'
          }`}
        >
          {t('text_event_expired')}
        </button>
      </div>

      {/* ── Activity Cards List ── */}
      {loading ? (
        <div className="flex flex-col gap-3 sm:gap-4">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="relative overflow-hidden bg-white rounded-2xl p-3.5 sm:p-4 border border-gray-100 shadow-sm flex flex-col justify-between animate-pulse"
            >
              {/* Top-Right Skeleton Corner Tag */}
              <div className="absolute top-0 right-0 w-16 h-6 bg-gray-100 rounded-bl-xl" />

              <div className="flex items-start gap-3 sm:gap-4">
                {/* Thumbnail Skeleton */}
                <div className="w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-xl bg-gray-200 shrink-0" />

                {/* Main Info Skeleton */}
                <div className="flex-1 min-w-0 pr-16 sm:pr-20 pt-1">
                  <div className="h-4 bg-gray-200 rounded-md w-11/12 mb-2.5" />
                  <div className="h-4 bg-gray-200 rounded-md w-3/5" />
                </div>
              </div>

              {/* Bottom Row Date Skeleton */}
              <div className="mt-3 pt-1 flex items-center justify-between">
                <div className="h-3.5 bg-gray-100 rounded-md w-48" />
              </div>
            </div>
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="py-16 text-center">
          <Empty description={t('text_no_activities')} />
        </div>
      ) : (
        <div className="flex flex-col gap-3 sm:gap-4">
          {events.map((item) => {
            const eventObj = (typeof item.eventsId === 'object' && item.eventsId !== null) ? item.eventsId : {};
            const title =
              item.localize?.[currentLang]?.title ||
              item.localize?.cn?.title ||
              eventObj.localize?.[currentLang]?.title ||
              eventObj.localize?.cn?.title ||
              eventObj.title ||
              '';

            const coverUrl =
              (eventObj.images && eventObj.images.length > 0 ? (eventObj.images[0]?.url || eventObj.images[0]) : null) ||
              (item.images && item.images.length > 0 ? (item.images[0]?.url || item.images[0]) : null) ||
              eventObj.picUrl ||
              item.picUrl ||
              null;

            const startDate = eventObj.startDate || item.startDate;
            const endDate = eventObj.endDate || item.endDate;
            const dateRangeText = startDate && endDate
              ? `${formatDate(startDate)}-${formatDate(endDate)}`
              : (startDate ? formatDate(startDate) : '');

            const isApproved = item.approvalStatus === 'approved';
            const showQrAction = activeTab !== 'expired' && isApproved && !item.isCheckedIn && (item.ticketCode || eventObj.ticketCode);

            return (
              <div
                key={item._id}
                className="relative overflow-hidden bg-white rounded-2xl p-3.5 sm:p-4 border border-gray-100 shadow-sm flex flex-col justify-between"
              >
                {/* Top-Right Corner Status Tag */}
                {renderStatusBadge(item)}

                <div className="flex items-start gap-3 sm:gap-4">
                  {/* Thumbnail Cover Image */}
                  <div className="w-[72px] h-[72px] sm:w-[80px] sm:h-[80px] rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-100">
                    {coverUrl ? (
                      <img
                        src={coverUrl}
                        alt={title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = BASE_URL + '/logo.svg';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-3 opacity-30">
                        <img src={BASE_URL + '/logo.svg'} alt="logo" className="w-full h-full object-contain" />
                      </div>
                    )}
                  </div>

                  {/* Main Info */}
                  <div className="flex-1 min-w-0 pr-16 sm:pr-20 pt-0.5">
                    {/* Event Title */}
                    <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug line-clamp-2 m-0">
                      {title}
                    </h3>
                  </div>
                </div>

                {/* Bottom Row: Date & Helper Note on Left, Big QR Code Icon on Right */}
                <div className="mt-3 pt-1 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm text-gray-700 m-0">
                      {t('text_event_time')} {dateRangeText}
                    </p>

                    {/* Helper Note for Confirmed Events */}
                    {showQrAction && (
                      <p className="mt-1 text-[11px] text-gray-400 flex items-center gap-1 m-0">
                        <span>ⓘ</span>
                        <span className="line-clamp-1">{t('text_click_view_qr')}</span>
                      </p>
                    )}
                  </div>

                  {/* QR Code Action Button */}
                  {showQrAction && (
                    <button
                      type="button"
                      onClick={(e) => handleOpenQr(e, item)}
                      className="p-1 -mr-1 rounded-xl text-gray-900 hover:text-[#e50a47] hover:bg-pink-50 transition-all cursor-pointer shrink-0 active:scale-95"
                      title={t('text_admission_qr')}
                      aria-label={t('text_admission_qr')}
                    >
                      <RiQrCodeLine size={36} className="text-gray-900 hover:text-[#e50a47] transition-colors" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Pagination ── */}
      {total > pageSize && (
        <div className="mt-6 flex justify-center">
          <CustomPagination
            total={total}
            pageSize={pageSize}
            pageNo={pageNo}
            onChange={(page) => setPageNo(page)}
          />
        </div>
      )}

      {/* ── Ticket QR Code Modal ── */}
      <EventQrModal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        event={selectedEvent}
      />
    </div>
  );
}
